// Drawola: reads and saves the app language choice. Fork-only.
//
// Only a manual choice is saved. An automatic pick from the browser languages
// is resolved again on every load, so a later browser change still counts.

import { languages } from "@excalidraw/excalidraw/i18n";

import type { Language } from "@excalidraw/excalidraw/i18n";

import { LANGUAGE_STORAGE_KEY, resolveAppLanguage } from "./resolveLanguage";

/** Upstream's dev-only pseudo languages share this prefix. */
const TEST_LANGUAGE_PREFIX = "__test__";

/** Keeps a storage error in the console short, whatever the browser throws. */
const MAX_DIAGNOSTIC_LENGTH = 160;

/**
 * A manual choice that storage did not keep, so it still wins on this page.
 * Module state on purpose: a new page starts without it.
 */
let unsavedManualLanguage: string | null = null;

/** The error message, cut to a length that keeps the console readable. */
const describeError = (error: unknown): string =>
  (error instanceof Error ? error.message : String(error)).slice(
    0,
    MAX_DIAGNOSTIC_LENGTH,
  );

/** The saved manual choice, or null when there is none or storage is denied. */
export const readSavedLanguage = (): string | null => {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch (error) {
    console.warn(
      `Language preference could not be read: ${describeError(error)}`,
    );
    return null;
  }
};

/**
 * Saves a manual choice. When storage denies it, drops it silently or cannot
 * read it back, the choice only lasts for this page.
 */
export const saveManualLanguage = (code: string): void => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch (error) {
    console.warn(
      `Language preference could not be saved: ${describeError(error)}`,
    );
    unsavedManualLanguage = code;
    return;
  }
  if (readSavedLanguage() === code) {
    unsavedManualLanguage = null;
    return;
  }
  console.warn(
    "Language preference could not be saved: stored value does not match",
  );
  unsavedManualLanguage = code;
};

/** The browser languages in the visitor's order of preference. */
export const browserLanguages = (): readonly string[] =>
  navigator.languages?.length
    ? navigator.languages
    : navigator.language
    ? [navigator.language]
    : [];

/** Languages the pickers offer in production, without dev pseudo languages. */
export const productionLanguages = (): Language[] =>
  languages.filter((lang) => !lang.code.startsWith(TEST_LANGUAGE_PREFIX));

/** Codes of `productionLanguages`. */
export const productionLanguageCodes = (): string[] =>
  productionLanguages().map((lang) => lang.code);

/**
 * The language to show now: this page's unsaved manual choice, the saved
 * manual choice, browser languages, English.
 */
export const resolvePreferredLanguage = (): string =>
  resolveAppLanguage(
    unsavedManualLanguage ?? readSavedLanguage(),
    browserLanguages(),
    productionLanguageCodes(),
  );
